// src/calculators/rent.js — калкулатор „Наем" (ЗДДФЛ), задача 4/6.
// Сметката е в services/rent.js (чиста функция, с тестове); тук е само
// екранът. Цитатите в колоната са СВЕРЕНИ ДОСЛОВНО в lex.bg (25.08.2026,
// редакция ДВ, бр. 30 от 27.03.2026 г.) — не преписани от справката.

import { el, clear, field, input, row } from '../shared/dom.js';
import { money } from '../shared/format.js';
import { withLaw, hintify } from './law.js';
import { setChl55Prefill } from './prefill.js';

// Сметката идва от services/ — но той е CommonJS, а екранът е ES модул.
// Vite се справя с require-стила през import (interop).
import { rentAdvanceTax, DISABILITY_THRESHOLD_EUR } from '../../services/rent.js';

const line = (label, value, big) => el('div', { class: 'calc-line' + (big ? ' big' : '') }, el('span', { text: label }), el('span', { text: value }));
const noteRow = (t) => el('div', { style: 'font-size:12px;color:var(--muted);padding:2px 0', text: '• ' + t });
const warnRow = (t) => el('div', { class: 'sev-warn', text: t });
const checkRow = (box, text) => el('div', { class: 'field' }, el('label', { class: 'choice' }, box, el('span', { text })));

export function buildRentCalc() {
  const now = new Date();
  const curQ = Math.floor(now.getMonth() / 3) + 1;
  const f = {
    gross: input({ type: 'number', step: '0.01', min: '0', placeholder: 'напр. 1000' }),
    quarter: el('select', {}, ...[1, 2, 3, 4].map((q) => el('option', { value: q, text: q + '-во тримесечие', selected: q === curQ }))),
    year: el('select', {}, ...[2025, 2026, 2027].map((y) => el('option', { value: y, text: String(y), selected: y === now.getFullYear() }))),
    payer: el('select', {},
      el('option', { value: 'enterprise', text: 'Предприятие / самоосигуряващо се лице (удържа данъка)', selected: true }),
      el('option', { value: 'person', text: 'Друго физическо лице — наемодателят сам внася' })),
    wantsQ4: el('input', { type: 'checkbox' }),
    reduced: el('input', { type: 'checkbox' }),
    ytd: input({ type: 'number', step: '0.01', min: '0', placeholder: 'от началото на годината' }),
    contributions: input({ type: 'number', step: '0.01', min: '0', placeholder: '0' }),
    foreign: el('input', { type: 'checkbox' }),
    condo: el('input', { type: 'checkbox' }),
  };
  // Условните полета: показват се само когато имат смисъл.
  const wantsQ4Row = checkRow(f.wantsQ4, 'Лицето ПИСМЕНО е декларирало, че желае удържане за IV тримесечие (чл. 65, ал. 13)');
  const ytdField = field('Облагаем доход от ВСИЧКИ източници от началото на годината', f.ytd, { size: 'm' });
  // Прагът е конфигурационна константа (services/rent.js) — тук само се показва.
  const reducedRow = checkRow(f.reduced, 'Наемодателят е с намалена работоспособност ≥ 50 % (праг ' + DISABILITY_THRESHOLD_EUR.toFixed(2).replace('.', ',') + ' € — чл. 44, ал. 2)');
  const contribField = field('Осигуровки за своя сметка (приспадат се — чл. 44, ал. 1)', f.contributions, { size: 'm' });

  const result = el('div', { class: 'calc-result' });
  const notesBox = el('div', { style: 'margin-top:8px' });
  const toChl55 = el('button', { class: 'btn sm ghost', type: 'button', text: 'Към декларацията по чл. 55 (с това тримесечие)', onClick: () => {
    setChl55Prefill({ quarter: Number(f.quarter.value), year: Number(f.year.value) });
    window.openCalc('chl55');
  } });
  const chl55Box = el('div', { style: 'margin-top:10px' }, toChl55);

  function recompute() {
    clear(result); clear(notesBox);
    const isQ4 = Number(f.quarter.value) === 4;
    const isEnterprise = f.payer.value === 'enterprise';
    wantsQ4Row.style.display = (isQ4 && isEnterprise) ? '' : 'none';
    ytdField.style.display = f.reduced.checked ? '' : 'none';
    contribField.style.display = isEnterprise ? 'none' : '';
    // Декларацията по чл. 55 съществува от задача 5 — дотогава бутонът се крие.
    chl55Box.style.display = (window.CALC_LABELS && window.CALC_LABELS.chl55) ? '' : 'none';

    const gross = Number(f.gross.value) || 0;
    if (gross <= 0) {
      result.appendChild(el('div', { class: 'calc-line', text: 'Въведете брутния наем за тримесечието.' }));
      return;
    }
    const r = rentAdvanceTax({
      grossRent: gross,
      quarter: Number(f.quarter.value),
      year: Number(f.year.value),
      payerIsEnterprise: isEnterprise,
      wantsQ4Withholding: f.wantsQ4.checked,
      reducedCapacity: f.reduced.checked,
      ytdTaxableIncome: Number(f.ytd.value) || 0,
      healthContributions: Number(f.contributions.value) || 0,
      foreignPerson: f.foreign.checked,
      condominium: f.condo.checked,
    });

    if (r.special) {
      // Случай, който калкулаторът НЕ смята — само предупреждава.
      r.notes.forEach((n) => result.appendChild(warnRow('⚠ ' + n)));
      return;
    }

    const m = (x) => money(x, r.currency);
    result.appendChild(line('Брутен наем', m(r.grossRent)));
    result.appendChild(line('Нормативно признати разходи 10 % (чл. 31, ал. 1)', '− ' + m(r.normativeExpenses)));
    result.appendChild(line('Облагаем доход', m(r.taxableIncome)));
    if (!f.payer.value.startsWith('enterprise') && (Number(f.contributions.value) || 0) > 0) {
      result.appendChild(line('Осигуровки за своя сметка (чл. 44, ал. 1)', '− ' + m(Number(f.contributions.value) || 0)));
    }
    result.appendChild(line('Данъчна основа', m(r.taxBase)));
    result.appendChild(line('Авансов данък 10 % (чл. 44)', m(r.tax)));
    result.appendChild(line('За изплащане на наемодателя', m(r.netPayable), true));
    const WHO = {
      payer: 'Платецът — удържа при изплащането (чл. 44, ал. 4)',
      person: 'Наемодателят — сам, с декларация по чл. 55, ал. 2',
      none: 'Никой за това тримесечие (IV тримесечие)',
    };
    result.appendChild(line('Кой внася данъка', WHO[r.withholder] || '—'));
    if (r.dueDate) {
      const p = r.dueDate.split('-');
      result.appendChild(line('Срок за внасяне и деклариране', p[2] + '.' + p[1] + '.' + p[0]));
    }
    r.notes.forEach((n) => notesBox.appendChild(/проверете при подаване/i.test(n) ? warnRow('⚠ ' + n) : noteRow(n)));
  }

  Object.values(f).forEach((n) => { n.addEventListener('input', recompute); n.addEventListener('change', recompute); });
  recompute();

  const panel = el('div', { class: 'panel calc-panel' },
    el('h3', { class: 'calc-title', text: 'Наем — авансов данък (чл. 31 и чл. 44 ЗДДФЛ)' }),
    row(hintify(field('Брутен наем за тримесечието', f.gross, { size: 's' }),
      'чл. 31, ал. 1 ЗДДФЛ', 'Облагаемият доход от наем е придобитият доход, намален с 10 на сто разходи.'),
      field('Тримесечие', f.quarter, { size: 'm' }), field('Година', f.year, { size: 's' })),
    field('Кой плаща наема', f.payer),
    wantsQ4Row,
    contribField,
    reducedRow,
    ytdField,
    checkRow(f.foreign, 'Наемодателят е ЧУЖДЕСТРАННО физическо лице'),
    checkRow(f.condo, 'Имотът е в режим на ЕТАЖНА СОБСТВЕНОСТ (общо събрание)'),
    result,
    notesBox,
    chl55Box);

  // TODO (обхват, решение на собственика): земеделска земя (необлагаема по
  // чл. 13, ал. 1, т. 24) и разпределение между съсобственици не влизат в
  // тази версия на калкулатора.
  return withLaw(panel, {
    when: [
      'Фирма-клиент (или СОЛ) плаща наем на физическо лице за офис, склад, кола — платецът удържа данъка при изплащането и подава чл. 55.',
      'Физическо лице отдава имот на друго физическо лице — наемодателят сам внася данъка на тримесечие и сам подава чл. 55.',
      'Четвърто тримесечие: платецът не удържа, освен ако лицето писмено поиска; лицето само — не внася изобщо.',
      'Наемодател с ТЕЛК ≥ 50 % — данък едва след прага по чл. 44, ал. 2.',
      'Сумите от 01.01.2026 г. са в евро; периоди до 31.12.2025 г. се декларират в левове.',
    ],
    quotes: [
      { ref: 'чл. 31, ал. 1 ЗДДФЛ (изм. ДВ, бр. 113/2007)', text: 'Облагаемият доход от наем или от друго възмездно предоставяне за ползване на движимо или недвижимо имущество се определя, като придобитият доход се намалява с 10 на сто разходи.' },
      { ref: 'чл. 44, ал. 1 ЗДДФЛ (изм. ДВ, бр. 113/2007)', text: 'Лице, придобило доход от наем или от друго възмездно предоставяне за ползване на права или имущество, дължи авансово данък върху разликата между облагаемия доход по чл. 31 и осигурителните вноски, които лицето е задължено да прави за своя сметка.' },
      { ref: 'чл. 44, ал. 3 ЗДДФЛ', text: 'Размерът на дължимия данък се определя, като разликата по ал. 1 се умножи по данъчна ставка 10 на сто.' },
      { ref: 'чл. 44, ал. 4 ЗДДФЛ (нова — ДВ, бр. 94/2010)', text: 'Когато платецът на дохода от наем или друго възмездно предоставяне за ползване на права или имущество е предприятие или самоосигуряващо се лице, размерът на данъка се определя и данъкът се удържа от платеца на дохода при изплащането му. В тези случаи размерът на дължимия авансов данък се определя, като облагаемият доход по чл. 31 се умножи по данъчна ставка 10 на сто.' },
      { ref: 'чл. 65, ал. 12 ЗДДФЛ (изм. ДВ, бр. 95/2015)', text: 'Данъкът, който предприятията и самоосигуряващите се лица – платци на доходи, са задължени да удържат по чл. 43, ал. 4, чл. 44, ал. 4 и чл. 44а, ал. 1, се внася в срок до края на месеца, следващ тримесечието на придобиване на дохода.' },
      { ref: 'чл. 65, ал. 13 ЗДДФЛ (изм. ДВ, бр. 95/2015)', text: 'Не се удържа и внася авансов данък по чл. 43, ал. 4, чл. 44, ал. 4 и чл. 44а, ал. 1 за доходи, придобити през четвъртото тримесечие на данъчната година, освен когато лицето, придобиващо дохода, писмено декларира пред предприятието или самоосигуряващото се лице – платец на дохода, че желае удържане на данък. В тези случаи удържаният данък се внася от платеца на дохода в срок до 31 януари на следващата година.' },
      { ref: 'чл. 45, ал. 4 ЗДДФЛ (изм. ДВ, бр. 98/2018)', text: 'Предприятие или самоосигуряващо се лице по смисъла на Кодекса за социално осигуряване – платец на доходи по чл. 29 или 31, издава за изплатените доходи и удържания данък по реда на чл. 43 и 44 сметка за изплатени суми по образец, която предоставя на лицето, придобило дохода, или на упълномощено от него лице.' },
    ],
  });
}
